import type React from "react";
import type { AudioAnalysisConfig } from "../../../../audio/audio-analyzer";

interface PreferenceAdvancedTabProps {
	audioConfig: AudioAnalysisConfig;
	lowConfidenceThreshold: number;
	onUpdateAudioConfig: (config: Partial<AudioAnalysisConfig>) => void;
	onUpdateLowConfidenceThreshold: (threshold: number) => void;
}

export const PreferenceAdvancedTab: React.FC<PreferenceAdvancedTabProps> = ({
	audioConfig,
	lowConfidenceThreshold,
	onUpdateAudioConfig,
	onUpdateLowConfidenceThreshold,
}) => {
	return (
		<div className="flex flex-col gap-8">
			<h3 className="m-0 mb-4 text-[1.1rem] text-primary border-b border-border-main pb-2">
				オーディオ解析設定
			</h3>
			<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
				<label
					className="text-xs font-semibold text-text-dim"
					htmlFor="silence-threshold-range"
				>
					無音検出しきい値: {audioConfig.silenceThreshold}ms
				</label>
				<input
					id="silence-threshold-range"
					className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main rounded-lg"
					type="range"
					min="100"
					max="2000"
					step="100"
					value={audioConfig.silenceThreshold || 1000}
					onChange={(e) =>
						onUpdateAudioConfig({
							silenceThreshold: parseInt(e.target.value, 10),
						})
					}
				/>
			</div>

			<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
				<label
					className="text-xs font-semibold text-text-dim"
					htmlFor="volume-threshold-range"
				>
					発話検出しきい値: {audioConfig.volumeThreshold}%
				</label>
				<input
					id="volume-threshold-range"
					className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main rounded-lg"
					type="range"
					min="1"
					max="50"
					step="1"
					value={audioConfig.volumeThreshold || 10}
					onChange={(e) =>
						onUpdateAudioConfig({
							volumeThreshold: parseInt(e.target.value, 10),
						})
					}
				/>
			</div>

			<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
				<label
					className="text-xs font-semibold text-text-dim"
					htmlFor="auto-resume-threshold-range"
				>
					自動再開しきい値: {audioConfig.autoResumeThreshold}%
				</label>
				<input
					id="auto-resume-threshold-range"
					className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main rounded-lg"
					type="range"
					min="1"
					max="100"
					step="1"
					value={audioConfig.autoResumeThreshold || 25}
					onChange={(e) =>
						onUpdateAudioConfig({
							autoResumeThreshold: parseInt(e.target.value, 10),
						})
					}
				/>
			</div>

			<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
				<label
					className="text-xs font-semibold text-text-dim"
					htmlFor="analysis-interval-range"
				>
					解析間隔: {audioConfig.analysisInterval}ms
				</label>
				<input
					id="analysis-interval-range"
					className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main rounded-lg"
					type="range"
					min="50"
					max="500"
					step="50"
					value={audioConfig.analysisInterval || 100}
					onChange={(e) =>
						onUpdateAudioConfig({
							analysisInterval: parseInt(e.target.value, 10),
						})
					}
				/>
			</div>

			<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
				<label
					className="text-xs font-semibold text-text-dim"
					htmlFor="smoothing-factor-range"
				>
					スムージング係数: {audioConfig.smoothingFactor}
				</label>
				<input
					id="smoothing-factor-range"
					className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main rounded-lg"
					type="range"
					min="0"
					max="1"
					step="0.05"
					value={audioConfig.smoothingFactor || 0.3}
					onChange={(e) =>
						onUpdateAudioConfig({
							smoothingFactor: parseFloat(e.target.value),
						})
					}
				/>
			</div>

			<h3 className="m-0 mt-8 mb-4 text-primary border-b border-border-main pb-2 text-[1.1rem]">
				音声認識設定
			</h3>
			<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
				<label
					className="text-xs font-semibold text-text-dim"
					htmlFor="confidence-threshold-range"
				>
					信頼度しきい値: {Math.round(lowConfidenceThreshold * 100)}%
				</label>
				<input
					id="confidence-threshold-range"
					className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main rounded-lg"
					type="range"
					min="0"
					max="1"
					step="0.05"
					value={lowConfidenceThreshold}
					onChange={(e) =>
						onUpdateLowConfidenceThreshold(parseFloat(e.target.value))
					}
				/>
			</div>
		</div>
	);
};
